import { useEffect, useRef, useState, type FormEvent } from 'react'
import './ToyDetailsModal.css'

export type ToyDetails = {
  title: string
  description: string
  thumbnailUrl?: string
}

type Props = {
  open: boolean
  initialValues?: ToyDetails
  onSubmit: (details: ToyDetails) => void
  onCancel: () => void
}

export default function ToyDetailsModal({ open, initialValues, onSubmit, onCancel }: Props) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [thumbnailUrl, setThumbnailUrl] = useState('')

  // Snapshot the latest initialValues without re-prefilling while the modal
  // is open (which would clobber whatever the user has typed).
  const initialValuesRef = useRef(initialValues)
  useEffect(() => {
    initialValuesRef.current = initialValues
  })

  useEffect(() => {
    const dlg = dialogRef.current
    if (!dlg) return
    if (open && !dlg.open) dlg.showModal()
    if (!open && dlg.open) dlg.close()
  }, [open])

  useEffect(() => {
    if (open) {
      const initial = initialValuesRef.current
      setTitle(initial?.title ?? '')
      setDescription(initial?.description ?? '')
      setThumbnailUrl(initial?.thumbnailUrl ?? '')
    }
  }, [open])

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    const t = title.trim()
    const d = description.trim()
    const u = thumbnailUrl.trim()
    if (!t || !d) return
    onSubmit({ title: t, description: d, thumbnailUrl: u || undefined })
  }

  return (
    <dialog
      ref={dialogRef}
      className="toy-modal"
      onCancel={(e) => {
        e.preventDefault()
        onCancel()
      }}
      onClose={() => {
        if (open) onCancel()
      }}
    >
      <form className="toy-modal__form" onSubmit={handleSubmit}>
        <h2 className="toy-modal__title">Tell us about your toy</h2>
        <p className="toy-modal__lede">
          This is what people will see when they browse your creation.
        </p>

        <label className="toy-modal__field">
          <span className="toy-modal__label">Title</span>
          <input
            type="text"
            className="toy-modal__input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            autoFocus
            maxLength={120}
          />
        </label>

        <label className="toy-modal__field">
          <span className="toy-modal__label">Description</span>
          <textarea
            className="toy-modal__textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
            rows={4}
            maxLength={500}
          />
        </label>

        <label className="toy-modal__field">
          <span className="toy-modal__label">
            Thumbnail URL <em className="toy-modal__optional">(optional)</em>
          </span>
          <input
            type="url"
            className="toy-modal__input"
            value={thumbnailUrl}
            onChange={(e) => setThumbnailUrl(e.target.value)}
            placeholder="https://…"
          />
        </label>

        <div className="toy-modal__actions">
          <button type="button" className="btn btn-ghost" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!title.trim() || !description.trim()}
          >
            Make it
          </button>
        </div>
      </form>
    </dialog>
  )
}
